/**
 * PERSONEL PORTALI — /portal (girişsiz alan, kendi kimliğiyle).
 *
 * Personelin kendi bilgileri, puantajı, izinleri, avansları ve hakedişi. SALT
 * OKUNUR; tek istisna izin ve avans talebi (bkz. routes/portal.py).
 *
 * `lib/api.js`'teki JWT'li istemci KULLANILMAZ (PublicPuantajForm ve
 * PublicEmployeeForm ile aynı gerekçe): bu sayfanın oturumu ERP kullanıcısı
 * değil, PERSONEL jetonudur ve ortak istemciye eklenecek bir "oturum gerekli"
 * mantığı burayı kırardı.
 *
 * DİL: TR/EN, sayfanın kendi sözlüğüyle (kullanıcı, 2026-09-18). Uygulamanın
 * geri kalanı tek dil; buraya i18n kütüphanesi sokmak, tek ekran için bütün
 * pakete yük bindirirdi. Personelin çoğu yabancı uyruklu olduğu için EN şart.
 * Seçim `localStorage`da: personel her girişte tekrar seçmesin.
 *
 * Oturum jetonu `localStorage`da ve 12 saatlik. `sessionStorage` daha güvenli
 * olurdu ama telefonda sekme her kapandığında yeniden e-posta kodu istemek
 * kullanılamaz hale getirirdi; jetonun kendi ömrü ve "Çıkış" düğmesi var.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  LogOut, Loader2, AlertTriangle, CheckCircle, CalendarDays, Wallet,
  Plane, User, Send, ChevronLeft, ChevronRight, Languages,
} from 'lucide-react';

const API = window.location.origin + '/api/erp/portal';
const JETON_ANAHTARI = 'toros_portal_jeton';
const DIL_ANAHTARI = 'toros_portal_dil';

const S = {
  tr: {
    baslik: 'Personel Ekranı', eposta: 'E-posta adresiniz', kodIste: 'Kod gönder',
    kod: 'E-postanıza gelen 6 haneli kod', giris: 'Giriş yap', geri: 'Geri',
    kodGonderildi: 'E-posta adresi kayıtlıysa tek kullanımlık kod gönderildi. Kod 10 dakika geçerlidir.',
    cikis: 'Çıkış', ozet: 'Özet', puantaj: 'Puantaj', izinler: 'İzinler',
    avans: 'Avans', hakedis: 'Hakediş',
    kisisel: 'Kişisel bilgiler', departman: 'Departman', pozisyon: 'Pozisyon',
    iseGiris: 'İşe giriş', telefon: 'Telefon', adres: 'Adres', banka: 'Banka',
    iban: 'Hesap / IBAN', tcKimlik: 'Kimlik no', dogum: 'Doğum tarihi',
    sgk: 'SGK no', acilDurum: 'Acil durumda aranacak',
    izinKalan: 'Kalan yıllık izin', izinHak: 'Hak edilen', izinKullanilan: 'Kullanılan',
    gun: 'gün', bakiye: 'Cari bakiye',
    bakiyeAciklama: 'Muhasebedeki cari hesabınızla aynı rakam: ödemeler eksi tahakkuklar. Ayın tahakkuku yapılmadan önce rakam olduğundan yüksek görünebilir.',
    bakiyeSifir: 'Bakiye kapalı',
    acilisUyari: 'Açılış bakiyesi henüz girilmediği için kalan izniniz olduğundan yüksek görünebilir.',
    donem: 'Dönem', onayli: 'Tahakkuk edildi', mesaiToplam: 'Toplam fazla mesai', saat: 'saat',
    kadroDisi: 'Kadro dışı',
    girilmemisNot: 'Çizgili günlerin puantajı henüz girilmemiş.',
    girilmeyen: 'Girilmemiş', bakiyeYok: 'Hareket yok',
    yeniIzin: 'Yeni izin talebi', izinTuru: 'İzin türü', baslangic: 'Başlangıç', bitis: 'Bitiş',
    aciklama: 'Açıklama', gonder: 'Talebi gönder', talepler: 'Taleplerim',
    yeniAvans: 'Yeni avans talebi', tutar: 'Tutar',
    durum: 'Durum', tarih: 'Tarih', tutarBaslik: 'Tutar', belge: 'Belge',
    tahakkuk: 'Tahakkuk', odeme: 'Ödeme', fark: 'Fark', hareketYok: 'Henüz hareket yok',
    talepYok: 'Henüz talebiniz yok', basarili: 'Talebiniz alındı, onay bekliyor.',
    hataGenel: 'Bir sorun oluştu, tekrar deneyin.', zorunlu: 'Lütfen alanları doldurun',
    beklemede: 'Beklemede', onaylandi: 'Onaylandı', reddedildi: 'Reddedildi', iptal: 'İptal',
    pending: 'Beklemede', approved: 'Onaylandı', rejected: 'Reddedildi',
    oturumBitti: 'Oturumunuz sona erdi, tekrar giriş yapın.',
  },
  en: {
    baslik: 'Employee Portal', eposta: 'Your e-mail address', kodIste: 'Send code',
    kod: '6-digit code sent to your e-mail', giris: 'Sign in', geri: 'Back',
    kodGonderildi: 'If the address is registered, a one-time code has been sent. It is valid for 10 minutes.',
    cikis: 'Sign out', ozet: 'Summary', puantaj: 'Timesheet', izinler: 'Leave',
    avans: 'Advance', hakedis: 'Earnings',
    kisisel: 'Personal details', departman: 'Department', pozisyon: 'Position',
    iseGiris: 'Start date', telefon: 'Phone', adres: 'Address', banka: 'Bank',
    iban: 'Account / IBAN', tcKimlik: 'ID number', dogum: 'Date of birth',
    sgk: 'Social security no', acilDurum: 'Emergency contact',
    izinKalan: 'Remaining annual leave', izinHak: 'Accrued', izinKullanilan: 'Used',
    gun: 'days', bakiye: 'Account balance',
    bakiyeAciklama: 'The same figure as your account in accounting: payments minus accruals. Before the month is closed it may look higher than it is.',
    bakiyeSifir: 'Settled',
    acilisUyari: 'The opening balance has not been entered yet, so your remaining leave may appear higher than it is.',
    donem: 'Period', onayli: 'Payroll closed', mesaiToplam: 'Total overtime', saat: 'hours',
    kadroDisi: 'Not employed',
    girilmemisNot: 'Striped days have not been recorded yet.',
    girilmeyen: 'Not recorded', bakiyeYok: 'No records',
    yeniIzin: 'New leave request', izinTuru: 'Leave type', baslangic: 'Start', bitis: 'End',
    aciklama: 'Note', gonder: 'Submit request', talepler: 'My requests',
    yeniAvans: 'New advance request', tutar: 'Amount',
    durum: 'Status', tarih: 'Date', tutarBaslik: 'Amount', belge: 'Document',
    tahakkuk: 'Accrued', odeme: 'Paid', fark: 'Difference', hareketYok: 'No records yet',
    talepYok: 'You have no requests yet', basarili: 'Your request has been received and is pending approval.',
    hataGenel: 'Something went wrong, please try again.', zorunlu: 'Please fill in the fields',
    beklemede: 'Pending', onaylandi: 'Approved', reddedildi: 'Rejected', iptal: 'Cancelled',
    pending: 'Pending', approved: 'Approved', rejected: 'Rejected',
    oturumBitti: 'Your session has expired, please sign in again.',
  },
};

// Puantaj durumlarının İngilizcesi — sunucu Türkçe etiket döndürüyor, EN burada
const DURUM_EN = {
  worked: 'Worked', weekly_leave: 'Weekly rest', annual_leave: 'Annual leave',
  public_holiday: 'Public holiday', public_holiday_worked: 'Worked on holiday',
  unpaid_leave: 'Unpaid leave', sick_leave: 'Sick leave',
  excuse_leave: 'Excused leave', absent: 'Absent',
};
const RENK = {
  worked: 'bg-green-600', weekly_leave: 'bg-blue-500', annual_leave: 'bg-amber-500',
  public_holiday: 'bg-cyan-500', public_holiday_worked: 'bg-cyan-800',
  unpaid_leave: 'bg-orange-700', sick_leave: 'bg-rose-500',
  excuse_leave: 'bg-teal-600', absent: 'bg-red-600',
};
const DURUM_RENGI = {
  beklemede: 'bg-amber-100 text-amber-800', pending: 'bg-amber-100 text-amber-800',
  onaylandi: 'bg-green-100 text-green-800', approved: 'bg-green-100 text-green-800',
  reddedildi: 'bg-red-100 text-red-800', rejected: 'bg-red-100 text-red-800',
  iptal: 'bg-[#8a7e70]/15 text-[#5c554b]',
};

// Bakiye PARA BİRİMİ BAŞINA gelir (cari ekranıyla aynı kural): tek bir TL
// rakamına indirmek, kur çevrimini kayda gömmek olurdu.
const bakiyeYaz = (bakiyeler, bos) => {
  const girdiler = Object.entries(bakiyeler || {}).filter(([, v]) => Math.abs(v) >= 0.005);
  if (!girdiler.length) return bos;
  return girdiler.map(([pb, v]) => paraYaz(v, pb)).join(' · ');
};
// NOT: "şirketin size borcu / sizin borcunuz" diye YORUMLAMIYORUZ. Tahakkuk
// ay sonunda kesildiği için ay içinde ödemeler öne geçiyor ve rakam, aslında
// alacaklı olan personele "borçlusunuz" gibi görünürdü. Rakam cari ekranıyla
// birebir aynı; anlamı açıklama satırında düz metinle veriliyor.
const paraYaz = (t, pb) => `${Number(t || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${pb || 'TRY'}`;
const saatYaz = (s) => (s > 0 ? `+${s}` : `−${Math.abs(s)}`);
const tarihYaz = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('.') : '—');

export default function PersonelPortal() {
  const [dil, setDil] = useState(() => localStorage.getItem(DIL_ANAHTARI) || 'tr');
  const [jeton, setJeton] = useState(() => localStorage.getItem(JETON_ANAHTARI) || '');
  const [ad, setAd] = useState('');
  const [sekme, setSekme] = useState('ozet');
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [mesaj, setMesaj] = useState(null);       // {tur, metin}
  const t = S[dil];

  const dilDegistir = () => {
    const y = dil === 'tr' ? 'en' : 'tr';
    setDil(y); localStorage.setItem(DIL_ANAHTARI, y);
  };

  const cikis = useCallback(() => {
    localStorage.removeItem(JETON_ANAHTARI);
    setJeton(''); setVeri(null); setAd(''); setSekme('ozet');
  }, []);

  const iste = useCallback(async (yol, secenek = {}) => {
    const r = await fetch(`${API}${yol}`, {
      ...secenek,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jeton}`, ...(secenek.headers || {}) },
    });
    if (r.status === 401) { cikis(); throw new Error('oturum'); }
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.detail || 'hata');
    return j;
  }, [jeton, cikis]);

  useEffect(() => {
    if (!jeton) return;
    setYukleniyor(true);
    iste('/ozet').then(setVeri).catch(() => {}).finally(() => setYukleniyor(false));
  }, [jeton, iste]);

  if (!jeton) {
    return <Giris t={t} dil={dil} dilDegistir={dilDegistir}
      basarili={(tok, isim) => { localStorage.setItem(JETON_ANAHTARI, tok); setJeton(tok); setAd(isim || ''); }} />;
  }

  const sekmeler = [
    { id: 'ozet', ad: t.ozet, ikon: User },
    { id: 'puantaj', ad: t.puantaj, ikon: CalendarDays },
    { id: 'izinler', ad: t.izinler, ikon: Plane },
    { id: 'avans', ad: t.avans, ikon: Send },
    { id: 'hakedis', ad: t.hakedis, ikon: Wallet },
  ];

  return (
    <div className="min-h-screen bg-[#f0ebe2] max-w-lg mx-auto pb-24">
      <header className="px-4 pt-5 pb-3 bg-[#faf7f2] border-b border-[#d0c4b0] sticky top-0 z-10">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-[#8a7e70] truncate">{veri?.sirket || ''}</p>
            <h1 className="text-xl font-bold text-[#1a1916] leading-tight truncate">
              {ad || [veri?.personel?.first_name, veri?.personel?.last_name].filter(Boolean).join(' ') || t.baslik}
            </h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" onClick={dilDegistir} data-testid="portal-dil"
              className="flex items-center gap-1 rounded-lg border border-[#d0c4b0] bg-white px-2 py-1.5 text-xs font-semibold text-[#3F3E39]">
              <Languages size={13} /> {dil === 'tr' ? 'EN' : 'TR'}
            </button>
            <button type="button" onClick={cikis} data-testid="portal-cikis"
              title={t.cikis} aria-label={t.cikis}
              className="rounded-lg border border-[#d0c4b0] bg-white p-2 text-[#8a7e70]">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      {mesaj && (
        <div className="px-3 pt-3">
          <div className={`flex gap-2 items-start rounded-lg px-3 py-2.5 text-sm ${mesaj.tur === 'ok'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="portal-mesaj">
            {mesaj.tur === 'ok' ? <CheckCircle size={18} className="shrink-0" /> : <AlertTriangle size={18} className="shrink-0" />}
            <span>{mesaj.metin}</span>
          </div>
        </div>
      )}

      <main className="px-3 py-3">
        {yukleniyor && !veri ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#8a7e70]" /></div>
        ) : (
          <>
            {sekme === 'ozet' && <Ozet t={t} veri={veri} />}
            {sekme === 'puantaj' && <Puantaj t={t} dil={dil} iste={iste} />}
            {sekme === 'izinler' && <Izinler t={t} iste={iste} setMesaj={setMesaj} />}
            {sekme === 'avans' && <Avans t={t} iste={iste} setMesaj={setMesaj} />}
            {sekme === 'hakedis' && <Hakedis t={t} iste={iste} />}
          </>
        )}
      </main>

      <nav className="fixed bottom-0 inset-x-0 max-w-lg mx-auto bg-[#faf7f2] border-t border-[#d0c4b0] flex">
        {sekmeler.map(s => (
          <button key={s.id} type="button" onClick={() => { setSekme(s.id); setMesaj(null); }}
            data-testid={`portal-sekme-${s.id}`}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium
              ${sekme === s.id ? 'text-[#1a1916]' : 'text-[#8a7e70]'}`}>
            <s.ikon size={18} className={sekme === s.id ? '' : 'opacity-70'} />
            {s.ad}
          </button>
        ))}
      </nav>
    </div>
  );
}

/** İki adımlı giriş: e-posta → kod. */
function Giris({ t, dil, dilDegistir, basarili }) {
  const [adim, setAdim] = useState('eposta');
  const [eposta, setEposta] = useState('');
  const [kod, setKod] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const [secenekler, setSecenekler] = useState(null);

  const kodIste = async () => {
    if (!eposta.includes('@')) { setHata(t.zorunlu); return; }
    setMesgul(true); setHata(null);
    try {
      const r = await fetch(`${API}/kod-iste`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eposta }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setHata(j.detail || t.hataGenel); return; }
      setBilgi(t.kodGonderildi); setAdim('kod');
    } catch { setHata(t.hataGenel); } finally { setMesgul(false); }
  };

  const gir = async (employee_id) => {
    if (kod.trim().length < 4) { setHata(t.zorunlu); return; }
    setMesgul(true); setHata(null);
    try {
      const r = await fetch(`${API}/giris`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eposta, kod: kod.trim(), employee_id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setHata(j.detail || t.hataGenel); return; }
      if (j.secim_gerekli) { setSecenekler(j.secim_gerekli); return; }
      basarili(j.token, j.ad);
    } catch { setHata(t.hataGenel); } finally { setMesgul(false); }
  };

  return (
    <div className="min-h-screen bg-[#f0ebe2] flex flex-col">
      <div className="flex justify-end p-3">
        <button type="button" onClick={dilDegistir} data-testid="portal-dil"
          className="flex items-center gap-1 rounded-lg border border-[#d0c4b0] bg-white px-2 py-1.5 text-xs font-semibold text-[#3F3E39]">
          <Languages size={13} /> {dil === 'tr' ? 'EN' : 'TR'}
        </button>
      </div>
      <div className="flex-1 flex items-start justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl border border-[#d0c4b0] bg-[#faf7f2] p-5 mt-6">
          <h1 className="text-xl font-bold text-[#1a1916] mb-4">{t.baslik}</h1>

          {hata && (
            <p className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2"
              data-testid="giris-hata">{hata}</p>
          )}
          {bilgi && adim === 'kod' && (
            <p className="mb-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm px-3 py-2">{bilgi}</p>
          )}

          {secenekler ? (
            <div className="space-y-2">
              {secenekler.map(s => (
                <button key={s.employee_id} type="button" onClick={() => gir(s.employee_id)}
                  className="w-full rounded-lg border border-[#d0c4b0] bg-white px-3 py-3 text-left text-sm">
                  {s.sirket}
                </button>
              ))}
            </div>
          ) : adim === 'eposta' ? (
            <>
              <label className="block text-sm text-[#3F3E39] mb-1">{t.eposta}</label>
              <input type="email" inputMode="email" autoComplete="email" value={eposta}
                onChange={(e) => setEposta(e.target.value)} data-testid="giris-eposta"
                onKeyDown={(e) => { if (e.key === 'Enter') kodIste(); }}
                className="w-full h-11 rounded-lg border border-[#d0c4b0] px-3 text-base bg-white" />
              <button type="button" onClick={kodIste} disabled={mesgul} data-testid="giris-kod-iste"
                className="mt-4 w-full h-11 rounded-xl bg-[#2e2d29] text-[#f5f0e8] font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
                {mesgul && <Loader2 size={16} className="animate-spin" />}{t.kodIste}
              </button>
            </>
          ) : (
            <>
              <label className="block text-sm text-[#3F3E39] mb-1">{t.kod}</label>
              <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={kod}
                onChange={(e) => setKod(e.target.value.replace(/\D/g, ''))} data-testid="giris-kod"
                onKeyDown={(e) => { if (e.key === 'Enter') gir(); }}
                className="w-full h-12 rounded-lg border border-[#d0c4b0] px-3 text-2xl tracking-[0.4em] text-center bg-white" />
              <button type="button" onClick={() => gir()} disabled={mesgul} data-testid="giris-yap"
                className="mt-4 w-full h-11 rounded-xl bg-[#2e2d29] text-[#f5f0e8] font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
                {mesgul && <Loader2 size={16} className="animate-spin" />}{t.giris}
              </button>
              <button type="button" onClick={() => { setAdim('eposta'); setKod(''); setHata(null); }}
                className="mt-2 w-full text-xs text-[#8a7e70] py-2">{t.geri}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Kart = ({ baslik, children }) => (
  <div className="rounded-xl border border-[#d0c4b0] bg-white p-3 mb-3">
    {baslik && <p className="text-xs font-bold uppercase tracking-wide text-[#a3968a] mb-2">{baslik}</p>}
    {children}
  </div>
);
const Satir = ({ etiket, deger }) => (
  <div className="flex justify-between gap-3 py-1 text-sm border-b border-[#f0ebe2] last:border-0">
    <span className="text-[#8a7e70]">{etiket}</span>
    <span className="text-[#1a1916] text-right break-words">{deger || '—'}</span>
  </div>
);

function Ozet({ t, veri }) {
  if (!veri) return null;
  const p = veri.personel || {};
  return (
    <>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl border border-[#d0c4b0] bg-white p-3">
          <p className="text-xs text-[#8a7e70]">{t.izinKalan}</p>
          <p className="text-2xl font-bold text-[#1a1916]">{veri.izin?.kalan ?? '—'}
            <span className="text-sm font-normal text-[#8a7e70]"> {t.gun}</span></p>
        </div>
        <div className="rounded-xl border border-[#d0c4b0] bg-white p-3">
          <p className="text-xs text-[#8a7e70]">{t.bakiye}</p>
          <p className="text-lg font-bold text-[#1a1916] break-words leading-tight">
            {bakiyeYaz(veri.cari?.bakiyeler, t.bakiyeYok)}
          </p>
          <p className="text-[10px] text-[#a3968a] leading-tight mt-0.5">{t.bakiyeAciklama}</p>
        </div>
      </div>
      {veri.izin && veri.izin.acilis_girildi === false && (
        <div className="mb-3 flex gap-2 items-start rounded-lg bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 px-3 py-2 text-xs text-[#5c554b]">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" /><span>{t.acilisUyari}</span>
        </div>
      )}
      <Kart baslik={t.kisisel}>
        <Satir etiket={t.departman} deger={veri.departman} />
        <Satir etiket={t.pozisyon} deger={veri.pozisyon} />
        <Satir etiket={t.iseGiris} deger={tarihYaz(p.hire_date)} />
        <Satir etiket={t.tcKimlik} deger={p.national_id} />
        <Satir etiket={t.dogum} deger={tarihYaz(p.birth_date)} />
        <Satir etiket={t.sgk} deger={p.insurance_number} />
        <Satir etiket={t.telefon} deger={p.phone} />
        <Satir etiket="E-posta" deger={p.email} />
        <Satir etiket={t.adres} deger={p.address} />
        <Satir etiket={t.banka} deger={p.bank_name} />
        <Satir etiket={t.iban} deger={p.bank_account_number} />
        <Satir etiket={t.acilDurum}
          deger={[p.emergency_contact_name, p.emergency_contact_phone].filter(Boolean).join(' · ')} />
      </Kart>
    </>
  );
}

function Puantaj({ t, dil, iste }) {
  const bugun = new Date();
  const [yil, setYil] = useState(bugun.getFullYear());
  const [ay, setAy] = useState(bugun.getMonth() + 1);
  const [d, setD] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => {
    setYukleniyor(true);
    iste(`/puantaj?yil=${yil}&ay=${ay}`).then(setD).catch(() => {}).finally(() => setYukleniyor(false));
  }, [iste, yil, ay]);

  const kaydir = (yon) => {
    let a = ay + yon, y = yil;
    if (a < 1) { a = 12; y -= 1; }
    if (a > 12) { a = 1; y += 1; }
    if (y > bugun.getFullYear() || (y === bugun.getFullYear() && a > bugun.getMonth() + 1)) return;
    setAy(a); setYil(y);
  };
  const etiket = (durum, trEtiket) => (dil === 'en' ? (DURUM_EN[durum] || durum) : trEtiket);

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => kaydir(-1)} data-testid="portal-ay-geri"
          className="rounded-lg border border-[#d0c4b0] bg-white p-2"><ChevronLeft size={16} /></button>
        <span className="text-sm font-semibold text-[#1a1916]">
          {String(ay).padStart(2, '0')}.{yil}
          {d?.onayli && <span className="ml-2 text-[10px] rounded px-1.5 py-0.5 bg-green-100 text-green-800">{t.onayli}</span>}
        </span>
        <button type="button" onClick={() => kaydir(1)} data-testid="portal-ay-ileri"
          className="rounded-lg border border-[#d0c4b0] bg-white p-2"><ChevronRight size={16} /></button>
      </div>
      {yukleniyor || !d ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#8a7e70]" /></div>
      ) : (
        <>
          <Kart>
            {d.ozet.length === 0 && d.girilmeyen === 0 && <p className="text-sm text-[#8a7e70]">{t.hareketYok}</p>}
            {d.ozet.map(o => (
              <div key={o.durum} className="flex items-center justify-between py-1 text-sm">
                <span className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${RENK[o.durum] || 'bg-[#d0c4b0]'}`} />
                  {etiket(o.durum, o.etiket)}
                </span>
                <span className="font-semibold">{o.gun} {t.gun}</span>
              </div>
            ))}
            {d.girilmeyen > 0 && (
              <div className="flex items-center justify-between py-1 text-sm">
                <span className="flex items-center gap-2 text-[#8a7e70]">
                  <span className="w-2.5 h-2.5 rounded-full border border-[#c0b6a5]" />
                  {t.girilmeyen}
                </span>
                <span className="font-semibold text-[#8a7e70]">{d.girilmeyen} {t.gun}</span>
              </div>
            )}
            {d.toplam_mesai > 0 && (
              <div className="flex items-center justify-between py-1 text-sm border-t border-[#f0ebe2] mt-1 pt-2">
                <span className="text-[#8a7e70]">{t.mesaiToplam}</span>
                <span className="font-semibold">{d.toplam_mesai} {t.saat}</span>
              </div>
            )}
          </Kart>
          <div className="grid grid-cols-7 gap-1">
            {d.gunler.map(g => {
              // Üç ayrı hal: GİRİLMİŞ (durum rengi), girilmemiş (çizgili, nötr),
              // kadro dışı / gelecek (soluk). Girilmemişi "çalıştı" gibi
              // boyamak personele olmamış bir şeyi olmuş gibi gösterirdi.
              const stil = !g.kadroda || g.gelecek
                ? 'bg-[#e8dfd0] text-[#bdb3a3]'
                : g.durum ? `${RENK[g.durum] || 'bg-[#d0c4b0]'} text-white`
                  : 'bg-[repeating-linear-gradient(135deg,#f5f0e8,#f5f0e8_3px,#e0d6c6_3px,#e0d6c6_6px)] text-[#8a7e70] border border-[#e0d6c6]';
              return (
                <div key={g.gun}
                  title={!g.kadroda ? t.kadroDisi : g.durum ? etiket(g.durum, d.durumlar[g.durum]) : t.girilmeyen}
                  className={`aspect-square rounded-md flex flex-col items-center justify-center text-[10px] font-bold ${stil}`}>
                  {g.gun}
                  {!!g.mesai_saat && <span className="font-normal">{saatYaz(g.mesai_saat)}</span>}
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-[#a3968a] mt-2">{t.girilmemisNot}</p>
        </>
      )}
    </>
  );
}

function Izinler({ t, iste, setMesaj }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ izin_turu_id: '', baslangic: '', bitis: '', aciklama: '' });
  const [mesgul, setMesgul] = useState(false);

  const yukle = useCallback(() => { iste('/izinler').then(setD).catch(() => {}); }, [iste]);
  useEffect(() => { yukle(); }, [yukle]);

  const gonder = async () => {
    if (!form.izin_turu_id || !form.baslangic) { setMesaj({ tur: 'hata', metin: t.zorunlu }); return; }
    setMesgul(true); setMesaj(null);
    try {
      await iste('/izin-talebi', { method: 'POST', body: JSON.stringify({ ...form, bitis: form.bitis || form.baslangic }) });
      setMesaj({ tur: 'ok', metin: t.basarili });
      setForm({ izin_turu_id: '', baslangic: '', bitis: '', aciklama: '' });
      yukle();
    } catch (e) {
      setMesaj({ tur: 'hata', metin: e.message === 'oturum' ? t.oturumBitti : (e.message || t.hataGenel) });
    } finally { setMesgul(false); }
  };

  if (!d) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#8a7e70]" /></div>;
  return (
    <>
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        {[[t.izinHak, d.bakiye?.hak_edilen], [t.izinKullanilan, d.bakiye?.kullanilan], [t.izinKalan, d.bakiye?.kalan]].map(([e, v]) => (
          <div key={e} className="rounded-xl border border-[#d0c4b0] bg-white p-2">
            <p className="text-[10px] text-[#8a7e70] leading-tight">{e}</p>
            <p className="text-lg font-bold text-[#1a1916]">{v ?? '—'}</p>
          </div>
        ))}
      </div>

      <Kart baslik={t.yeniIzin}>
        <select value={form.izin_turu_id} onChange={(e) => setForm(f => ({ ...f, izin_turu_id: e.target.value }))}
          data-testid="izin-tur" className="w-full h-10 rounded-lg border border-[#d0c4b0] bg-white px-2 text-sm mb-2">
          <option value="">{t.izinTuru}…</option>
          {(d.turler || []).map(x => <option key={x.id} value={x.id}>{x.ad}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-[#8a7e70]">{t.baslangic}
            <input type="date" value={form.baslangic} data-testid="izin-baslangic"
              onChange={(e) => setForm(f => ({ ...f, baslangic: e.target.value }))}
              className="w-full h-10 rounded-lg border border-[#d0c4b0] bg-white px-2 text-sm mt-0.5" />
          </label>
          <label className="text-xs text-[#8a7e70]">{t.bitis}
            <input type="date" value={form.bitis} data-testid="izin-bitis"
              onChange={(e) => setForm(f => ({ ...f, bitis: e.target.value }))}
              className="w-full h-10 rounded-lg border border-[#d0c4b0] bg-white px-2 text-sm mt-0.5" />
          </label>
        </div>
        <input type="text" placeholder={t.aciklama} value={form.aciklama}
          onChange={(e) => setForm(f => ({ ...f, aciklama: e.target.value }))}
          className="w-full h-10 rounded-lg border border-[#d0c4b0] bg-white px-2 text-sm mt-2" />
        <button type="button" onClick={gonder} disabled={mesgul} data-testid="izin-gonder"
          className="mt-3 w-full h-11 rounded-xl bg-[#2e2d29] text-[#f5f0e8] font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
          {mesgul && <Loader2 size={16} className="animate-spin" />}{t.gonder}
        </button>
      </Kart>

      <Kart baslik={t.talepler}>
        {(d.talepler || []).length === 0 && <p className="text-sm text-[#8a7e70]">{t.talepYok}</p>}
        {(d.talepler || []).map(x => (
          <div key={x.id} className="flex items-center justify-between gap-2 py-2 border-b border-[#f0ebe2] last:border-0">
            <div className="min-w-0">
              <p className="text-sm text-[#1a1916]">{x.tur_adi || ''}</p>
              <p className="text-xs text-[#8a7e70]">{tarihYaz(x.baslangic)} – {tarihYaz(x.bitis)}</p>
            </div>
            <span className={`text-[11px] rounded px-2 py-1 shrink-0 ${DURUM_RENGI[x.durum] || ''}`}>
              {t[x.durum] || x.durum}
            </span>
          </div>
        ))}
      </Kart>
    </>
  );
}

function Avans({ t, iste, setMesaj }) {
  const [liste, setListe] = useState(null);
  const [tutar, setTutar] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [mesgul, setMesgul] = useState(false);

  const yukle = useCallback(() => { iste('/avanslar').then(setListe).catch(() => {}); }, [iste]);
  useEffect(() => { yukle(); }, [yukle]);

  const gonder = async () => {
    if (!tutar) { setMesaj({ tur: 'hata', metin: t.zorunlu }); return; }
    setMesgul(true); setMesaj(null);
    try {
      await iste('/avans-talebi', { method: 'POST', body: JSON.stringify({ amount: tutar, description: aciklama }) });
      setMesaj({ tur: 'ok', metin: t.basarili });
      setTutar(''); setAciklama(''); yukle();
    } catch (e) {
      setMesaj({ tur: 'hata', metin: e.message === 'oturum' ? t.oturumBitti : (e.message || t.hataGenel) });
    } finally { setMesgul(false); }
  };

  return (
    <>
      <Kart baslik={t.yeniAvans}>
        <input type="number" inputMode="decimal" step="any" min="0" placeholder={t.tutar} value={tutar}
          onChange={(e) => setTutar(e.target.value)} data-testid="avans-tutar"
          className="w-full h-11 rounded-lg border border-[#d0c4b0] bg-white px-3 text-base" />
        <input type="text" placeholder={t.aciklama} value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          className="w-full h-10 rounded-lg border border-[#d0c4b0] bg-white px-3 text-sm mt-2" />
        <button type="button" onClick={gonder} disabled={mesgul} data-testid="avans-gonder"
          className="mt-3 w-full h-11 rounded-xl bg-[#2e2d29] text-[#f5f0e8] font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
          {mesgul && <Loader2 size={16} className="animate-spin" />}{t.gonder}
        </button>
      </Kart>
      <Kart baslik={t.talepler}>
        {liste === null && <Loader2 className="animate-spin text-[#8a7e70]" />}
        {liste?.length === 0 && <p className="text-sm text-[#8a7e70]">{t.talepYok}</p>}
        {(liste || []).map(a => (
          <div key={a.id} className="flex items-center justify-between gap-2 py-2 border-b border-[#f0ebe2] last:border-0">
            <div>
              <p className="text-sm font-semibold text-[#1a1916]">{paraYaz(a.amount, a.currency)}</p>
              <p className="text-xs text-[#8a7e70]">{tarihYaz(a.request_date)}{a.description ? ` · ${a.description}` : ''}</p>
            </div>
            <span className={`text-[11px] rounded px-2 py-1 shrink-0 ${DURUM_RENGI[a.status] || ''}`}>
              {t[a.status] || a.status}
            </span>
          </div>
        ))}
      </Kart>
    </>
  );
}

function Hakedis({ t, iste }) {
  const [d, setD] = useState(null);
  const [acik, setAcik] = useState(null);
  useEffect(() => { iste('/hakedis').then(setD).catch(() => {}); }, [iste]);

  if (!d) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#8a7e70]" /></div>;
  return (
    <>
      <Kart>
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#8a7e70]">{t.bakiye}</span>
          <span className="text-lg font-bold text-[#1a1916] text-right break-words">
            {bakiyeYaz(d.bakiyeler, t.bakiyeYok)}
          </span>
        </div>
        <p className="text-[10px] text-[#a3968a] mt-1">{t.bakiyeAciklama}</p>
      </Kart>
      {d.aylar.length === 0 && <Kart><p className="text-sm text-[#8a7e70]">{t.hareketYok}</p></Kart>}
      {d.aylar.map(a => (
        <div key={a.ay} className="rounded-xl border border-[#d0c4b0] bg-white mb-2 overflow-hidden">
          <button type="button" onClick={() => setAcik(acik === a.ay ? null : a.ay)}
            data-testid={`hakedis-ay-${a.ay}`} className="w-full px-3 py-2.5 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-[#1a1916]">{a.ay}</span>
            <span className="text-xs text-[#8a7e70] text-right">
              {t.tahakkuk} {bakiyeYaz(a.tahakkuk, '—')}<br />{t.odeme} {bakiyeYaz(a.odeme, '—')}
            </span>
          </button>
          {acik === a.ay && (
            <div className="px-3 pb-3 border-t border-[#f0ebe2]">
              {a.hareketler.map((h, i) => (
                <div key={i} className="flex items-start justify-between gap-2 py-1.5 border-b border-[#f0ebe2] last:border-0">
                  <div className="min-w-0">
                    <p className="text-xs text-[#1a1916] break-words">{h.aciklama || h.belge || '—'}</p>
                    <p className="text-[10px] text-[#8a7e70]">{tarihYaz(h.tarih)}</p>
                  </div>
                  <span className={`text-sm font-semibold shrink-0 ${h.tur === 'debt' ? 'text-[#1a1916]' : 'text-[#3d6e4e]'}`}>
                    {h.tur === 'debt' ? '' : '−'}{paraYaz(h.tutar, h.para_birimi)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
